<template>
  <div class="rule-editor">
    <!-- Tier 1: pick an approach -->
    <div class="tier">
      <span class="tier-label">Approach</span>
      <div class="approach-row">
        <label
          v-for="opt in APPROACHES"
          :key="opt.value"
          :class="['approach-card', { active: currentApproach === opt.value }]"
        >
          <input
            type="radio"
            :value="opt.value"
            :checked="currentApproach === opt.value"
            @change="setApproach(opt.value)"
          />
          <span class="approach-title">{{ opt.title }}</span>
          <span class="approach-desc">{{ opt.description }}</span>
        </label>
      </div>
    </div>

    <!-- Tier 2: only when approach = "specific" -->
    <div v-if="currentApproach === 'specific'" class="tier tier-specific">
      <span class="tier-label">Specific arrangement</span>
      <div class="rotation-row">
        <label
          v-for="opt in availableRotationKinds"
          :key="opt.value"
          :class="['kind-pill', { active: localRule.kind === opt.value }]"
        >
          <input
            type="radio"
            :value="opt.value"
            :checked="localRule.kind === opt.value"
            @change="setKind(opt.value)"
          />
          <span>{{ opt.label }}</span>
        </label>
      </div>

      <div v-if="localRule.kind === 'fixed'" class="rule-detail">
        <span class="detail-label">With</span>
        <select :value="localRule.parent" @change="setField('parent', $event.target.value)">
          <option value="A">{{ partyAName || 'Party A' }}</option>
          <option value="B">{{ partyBName || 'Party B' }}</option>
        </select>
      </div>

      <div v-else-if="localRule.kind === 'alternating-by-year'" class="rule-detail">
        <span class="detail-label">Even years with</span>
        <select :value="localRule.evenParent" @change="setField('evenParent', $event.target.value)">
          <option value="A">{{ partyAName || 'Party A' }}</option>
          <option value="B">{{ partyBName || 'Party B' }}</option>
        </select>
        <span class="detail-hint">odd years go to the other parent</span>
      </div>

      <div v-else-if="localRule.kind === 'split-day'" class="rule-detail">
        <span class="detail-label">First half with</span>
        <select :value="localRule.firstHalfParent" @change="setField('firstHalfParent', $event.target.value)">
          <option value="A">{{ partyAName || 'Party A' }}</option>
          <option value="B">{{ partyBName || 'Party B' }}</option>
        </select>
        <template v-if="!localRule.transitionByAgreement">
          <span class="detail-label">Transition at</span>
          <input type="time" :value="localRule.transitionTime" @input="setField('transitionTime', $event.target.value)" />
        </template>
        <label class="inline-check">
          <input
            type="checkbox"
            :checked="!!localRule.transitionByAgreement"
            @change="setField('transitionByAgreement', $event.target.checked)"
          />
          <span>Transition time to be agreed by the parties</span>
        </label>
      </div>

      <div v-else-if="localRule.kind === 'split-period'" class="rule-detail">
        <span class="detail-label">First half of the break with</span>
        <select :value="localRule.firstHalfParent" @change="setField('firstHalfParent', $event.target.value)">
          <option value="A">{{ partyAName || 'Party A' }}</option>
          <option value="B">{{ partyBName || 'Party B' }}</option>
        </select>
        <span class="detail-hint">second half goes to the other parent</span>
      </div>

      <div v-else-if="localRule.kind === 'week-on-off'" class="rule-detail">
        <span class="detail-label">First week with</span>
        <select :value="localRule.startParent" @change="setField('startParent', $event.target.value)">
          <option value="A">{{ partyAName || 'Party A' }}</option>
          <option value="B">{{ partyBName || 'Party B' }}</option>
        </select>
        <span class="detail-hint">parents alternate week-by-week through the break</span>
      </div>

      <div v-else-if="localRule.kind === 'paired-day-split'" class="rule-detail">
        <span class="detail-label">Even years with</span>
        <select :value="localRule.evenParent" @change="setField('evenParent', $event.target.value)">
          <option value="A">{{ partyAName || 'Party A' }}</option>
          <option value="B">{{ partyBName || 'Party B' }}</option>
        </select>
        <span class="detail-label">Split at</span>
        <input type="time" :value="localRule.splitTime || '12:00'" @input="setField('splitTime', $event.target.value)" />
        <span class="detail-hint">{{ pairedRangeHint }} · odd years go to the other parent</span>
      </div>

      <!-- Scope toggle: only for long-weekend-eligible holidays + scopable kinds -->
      <div v-if="showScopeToggle" class="scope-toggle">
        <span class="detail-label">Apply to</span>
        <label class="scope-pill" :class="{ active: currentScope === 'holiday' }">
          <input type="radio" :checked="currentScope === 'holiday'" @change="setScope('holiday')" />
          <span>Just the holiday</span>
        </label>
        <label class="scope-pill" :class="{ active: currentScope === 'long-weekend' }">
          <input type="radio" :checked="currentScope === 'long-weekend'" @change="setScope('long-weekend')" />
          <span>The whole long weekend</span>
        </label>
        <p v-if="longWeekendHint" class="scope-hint">{{ longWeekendHint }}</p>
      </div>

      <!-- Long-weekend explanation when "Tie to weekend parent" is active -->
      <p v-if="localRule.kind === 'tie-to-weekend' && longWeekendHint" class="scope-hint scope-hint-block">
        {{ longWeekendHint }}
      </p>
    </div>
  </div>
</template>

<script>
import { SPECIFIC_RULE_KINDS, PAIRED_DAY_RANGES } from '../seed/holidays.js'

const APPROACHES = [
  {
    value: 'no-change',
    title: "Don't change the schedule",
    description: "The base parenting-time schedule continues; this day isn't treated specially.",
  },
  {
    value: 'suspend-flex',
    title: 'Take a flexible approach',
    description: 'Pause the regular schedule for this day; the parties decide together with reasonable notice.',
  },
  {
    value: 'specific',
    title: 'Set a specific arrangement',
    description: 'Lock in how this day is handled (alternate, split, share, or always one parent).',
  },
]

// Default rotation set for single-day holidays / special days / christmas.
const ROTATION_KINDS = [
  { value: 'fixed',                label: 'Always one parent' },
  { value: 'alternating-by-year',  label: 'Alternate annually' },
  { value: 'split-day',            label: 'Split the day' },
  { value: 'share',                label: 'Share together' },
  { value: 'paired-day-split',     label: 'Eve/Day split, alt.', pairedDayOnly: true },
  { value: 'tie-to-weekend',       label: 'Tie to weekend parent', longWeekendOnly: true },
]

// School breaks are multi-day, so they get a different set of options:
// no split-day, no tie-to-weekend, but add split-period and week-on-off.
const SCHOOL_BREAK_KINDS = [
  { value: 'fixed',                label: 'Always one parent' },
  { value: 'alternating-by-year',  label: 'Alternate annually' },
  { value: 'split-period',         label: 'Split the holiday' },
  { value: 'share',                label: 'Share as agreed' },
  { value: 'week-on-off',          label: 'Week on/off rotation' },
]

// Kinds that accept a `scope: 'holiday' | 'long-weekend'` field
// (only relevant for long-weekend-eligible holidays).
const SCOPABLE_KINDS = new Set(['fixed', 'alternating-by-year', 'share'])

// Defaults when entering each kind for the first time.
const DEFAULTS = {
  'no-change':           () => ({ kind: 'no-change' }),
  'suspend-flex':        () => ({ kind: 'suspend-flex' }),
  'fixed':               () => ({ kind: 'fixed', parent: 'A' }),
  'alternating-by-year': () => ({ kind: 'alternating-by-year', evenParent: 'A' }),
  'split-day':           () => ({ kind: 'split-day', firstHalfParent: 'A', transitionTime: '12:00' }),
  'split-period':        () => ({ kind: 'split-period', firstHalfParent: 'A' }),
  'share':               () => ({ kind: 'share' }),
  'week-on-off':         () => ({ kind: 'week-on-off', startParent: 'A' }),
  'paired-day-split':    () => ({ kind: 'paired-day-split', evenParent: 'A', splitTime: '12:00' }),
  'tie-to-weekend':      () => ({ kind: 'tie-to-weekend' }),
}

const PAIRED_DAY_IDS = new Set(['christmas-eve', 'christmas-day', 'new-years-eve', 'new-years-day'])

export default {
  name: 'RuleEditor',
  props: {
    value: { type: Object, required: true },
    partyAName: { type: String, default: '' },
    partyBName: { type: String, default: '' },
    longWeekendType: { type: String, default: null },
    category: { type: String, default: '' },
    holidayId: { type: String, default: '' },
  },
  data() {
    return { APPROACHES, ROTATION_KINDS }
  },
  computed: {
    localRule() {
      return this.value
    },
    currentApproach() {
      const k = this.localRule.kind
      if (k === 'no-change') return 'no-change'
      if (k === 'suspend-flex') return 'suspend-flex'
      if (SPECIFIC_RULE_KINDS.includes(k) || k === 'tie-to-weekend') return 'specific'
      return null
    },
    availableRotationKinds() {
      const set = this.category === 'school-break' ? SCHOOL_BREAK_KINDS : ROTATION_KINDS
      return set.filter((k) => {
        if (k.longWeekendOnly && !this.longWeekendType) return false
        if (k.pairedDayOnly && !PAIRED_DAY_IDS.has(this.holidayId)) return false
        return true
      })
    },
    showScopeToggle() {
      return this.longWeekendType && SCOPABLE_KINDS.has(this.localRule.kind)
    },
    currentScope() {
      return this.localRule.scope === 'long-weekend' ? 'long-weekend' : 'holiday'
    },
    longWeekendHint() {
      if (!this.longWeekendType) return ''
      if (this.longWeekendType === 'always-monday') return 'This holiday always falls on a Monday — adjacent weekend is Saturday & Sunday.'
      if (this.longWeekendType === 'always-friday') return 'This holiday always falls on a Friday — adjacent weekend is Saturday & Sunday.'
      if (this.longWeekendType === 'sometimes') return 'Only forms a long weekend in years where the date lands on a Monday or Friday.'
      return ''
    },
    pairedRangeHint() {
      const range = PAIRED_DAY_RANGES[this.holidayId]
      if (!range) return '24-hour Eve/Day block'
      return `${range.from} → ${range.to}, alternating yearly`
    },
  },
  methods: {
    setApproach(approachValue) {
      if (approachValue === this.currentApproach) return
      if (approachValue === 'specific') {
        this.$emit('input', DEFAULTS['alternating-by-year']())
      } else {
        this.$emit('input', DEFAULTS[approachValue]())
      }
    },
    setKind(kind) {
      if (kind === this.localRule.kind) return
      this.$emit('input', DEFAULTS[kind]())
    },
    setField(field, value) {
      this.$emit('input', { ...this.localRule, [field]: value })
    },
    setScope(scope) {
      if (this.currentScope === scope) return
      const next = { ...this.localRule, scope }
      if (scope === 'holiday') delete next.scope
      this.$emit('input', next)
    },
  },
}
</script>

<style scoped>
.rule-editor {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.tier {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}
.tier-specific {
  padding-top: 0.7rem;
  border-top: 1px dashed #e2e8f0;
}
.tier-label {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  font-weight: 600;
}

/* Tier 1: approach cards (3 cards in a row) */
.approach-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}
@media (max-width: 700px) {
  .approach-row { grid-template-columns: 1fr; }
}
.approach-card {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 0.55rem 0.7rem;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.approach-card:hover { border-color: #94a3b8; }
.approach-card input { display: none; }
.approach-card.active {
  background: #eef3f4;
  border-color: #3f6772;
  box-shadow: inset 0 0 0 1px #3f6772;
}
.approach-title {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1e293b;
}
.approach-card.active .approach-title { color: #34555f; }
.approach-desc {
  font-size: 0.72rem;
  color: #64748b;
  line-height: 1.35;
}

/* Tier 2: rotation pills */
.rotation-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
.kind-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.65rem;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  font-size: 0.78rem;
  color: #475569;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}
.kind-pill:hover { border-color: #94a3b8; }
.kind-pill input { display: none; }
.kind-pill.active {
  background: #3f6772;
  border-color: #3f6772;
  color: #fff;
}

.rule-detail {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  font-size: 0.82rem;
  color: #475569;
  margin-top: 0.25rem;
}
.detail-label { font-weight: 500; color: #64748b; }
.detail-hint {
  font-size: 0.75rem;
  color: #94a3b8;
  font-style: italic;
}
.rule-detail select,
.rule-detail input[type="time"] {
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #1e293b;
  padding: 0.3rem 0.5rem;
  font-size: 0.82rem;
  font-family: inherit;
}
/* Inline checkbox inside a rule-detail row, e.g. "Transition time to be
   agreed by the parties" for split-day. Sits next to selects/inputs. */
.rule-detail .inline-check {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  color: #34555f;
  cursor: pointer;
}
.rule-detail .inline-check input[type="checkbox"] {
  margin: 0;
  cursor: pointer;
}

/* Scope toggle (long-weekend-eligible holidays only) */
.scope-toggle {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.35rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #e2e8f0;
}
.scope-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.6rem;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  font-size: 0.75rem;
  color: #475569;
  cursor: pointer;
  user-select: none;
}
.scope-pill:hover { border-color: #94a3b8; }
.scope-pill input { display: none; }
.scope-pill.active {
  background: #557d89;
  border-color: #557d89;
  color: #fff;
}
.scope-hint {
  flex-basis: 100%;
  margin: 0.3rem 0 0;
  font-size: 0.72rem;
  color: #94a3b8;
  font-style: italic;
}
.scope-hint-block {
  margin-top: 0.6rem;
  padding: 0.4rem 0.55rem;
  background: #eef3f4;
  border-radius: 6px;
  font-style: normal;
  color: #557d89;
  font-size: 0.75rem;
}
</style>
